/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { Modal, ModalBody, ModalHeader } from '@msdyn365-commerce-modules/utilities';

import { IBuyboxGetDimensionVariantAvailabilityActionData } from '../../../data-actions/buybox-get-dimension-variants-availability-action';
import { IModalDimensions } from './buybox-product-configure-mfrm';

import classnames from 'classnames';

export interface IMfrmModalDrawerViewProps {
    isOpen?: boolean;
    className?: string;
    isMobile: boolean;
    modalDimensions?: IModalDimensions[];
    preSelection?: IModalDimensions;
    variantAvailibilityList: IBuyboxGetDimensionVariantAvailabilityActionData[];
    outOfStockText: string;
    percentFunc(dimensionPrice: number, discountAmount: number): void;
    formatCurrencyFunc(dimPrice: number | undefined): void;
    addItemClick(
        selectId: string,
        selectedValue: string,
        dropdownId: string,
        selectedDimensionLabel?: string,
        isAddItemClick?: boolean
    ): void;
    dimensionClickFunc?(): void;
    onCloseModal?(): void;
}
// tslint:disable-next-line:max-func-body-length
export const BuyBoxSizeComponent: React.FC<IMfrmModalDrawerViewProps> = props => {
    const { preSelection } = props;
    const isPreSelected = preSelection && preSelection.selectId.length && preSelection.selectedValue.length ? true : false;
    const [selectId, setSelectId] = React.useState<string | undefined>();
    const [preSelectionPending, setPreSelectionPending] = React.useState<boolean>(true);

    const handleKeyPress = (
        e: React.KeyboardEvent<HTMLElement>,
        highlightId: string,
        highlightValue: string,
        highlightDimensionLabel?: string
    ) => {
        if (e.keyCode === 13) {
            updatePricing(highlightId, highlightValue, highlightDimensionLabel, true);
        }
    };

    const updatePricing = (highlightId: string, highlightValue: string, highlightDimensionLabel?: string, isAddItemClick?: boolean) => {
        const selectHighlightId = highlightId;
        const selectedValue = highlightValue;
        const selectedDimensionLabel = highlightDimensionLabel;
        setSelectId(highlightId);

        let discountAmount = 0;
        let dimensionPrice = 0;

        props.modalDimensions &&
            props.modalDimensions.map(i => {
                if (preSelectionPending) {
                    if (i.selectedValue === preSelection?.selectedValue) {
                        discountAmount = i.discountAmount || 0;
                        dimensionPrice = i.dimensionPrice || 0;
                    }
                } else {
                    if (i.selectedValue === selectedValue) {
                        discountAmount = i.discountAmount || 0;
                        dimensionPrice = i.dimensionPrice || 0;
                    }
                }
            });

        if (preSelectionPending) {
            if (isPreSelected) {
                props.percentFunc(dimensionPrice, discountAmount);
            }
        } else {
            props.percentFunc(dimensionPrice, discountAmount);
        }

        if (highlightId) {
            selectHighlightId &&
                selectedValue &&
                props.modalDimensions &&
                props.addItemClick(
                    selectHighlightId,
                    selectedValue,
                    props.modalDimensions[0].dropdownId,
                    selectedDimensionLabel,
                    isAddItemClick
                );
        } else {
            preSelection &&
                props.modalDimensions &&
                props.addItemClick(
                    preSelection.selectId,
                    preSelection.selectedValue,
                    props.modalDimensions[0].dropdownId,
                    preSelection.dimensionLabel,
                    isAddItemClick
                );
        }
    };

    const returnValidPricing = (discountAmount: number, dimensionPrice: number) => {
        return discountAmount && dimensionPrice
            ? props.formatCurrencyFunc(dimensionPrice - discountAmount)
            : dimensionPrice
                ? props.formatCurrencyFunc(dimensionPrice)
                : '';
    };


    // if a pre-selection was made
    if (preSelection && isPreSelected && preSelectionPending) {
        updatePricing(preSelection.selectId, preSelection.selectedValue, preSelection.dimensionLabel, false);
        setPreSelectionPending(false);
    }

    return (
        <Modal
            autoFocus={true}
            fade={false}
            isOpen={props.isOpen}
            horizontalPosition={props.isMobile ? 'center' : 'right'}
            verticalPosition={props.isMobile ? 'center' : 'top'}
            zIndex={5003}
            toggle={props.onCloseModal}
            applicationNode={'rsg-root'}
            modalClassName={classnames(props.isMobile ? 'modal-buybox-mobile' : 'modal-buybox-desktop', !props.isOpen && 'not-shown')}
        >
            <ModalHeader toggle={props.onCloseModal} />
            <ModalBody>
                <h2 className='msc-title'>Choose a Size</h2>
                {props.modalDimensions && props.modalDimensions.map((dimension: IModalDimensions) => {
                    let isAvailable = false;
                    for (let idx = 0; idx < props.variantAvailibilityList.length; idx++) {
                        // tslint:disable-next-line: radix
                        const sameSizeVariantsAvailability = props.variantAvailibilityList.filter((variant => variant.sizeDimensionRecordID === parseInt(dimension.selectId, 10)));
                        isAvailable = sameSizeVariantsAvailability.find(variant => variant.isAvailable) ? true : false;
                    }
                    return (
                        <div
                            className={dimension.selectId === selectId ? 'msc-dimitem-selected msc-variation selected' : 'msc-dimitem msc-variation'}
                            id={dimension.selectId}
                            key={dimension.selectId}
                            role='button'
                            tabIndex={0}
                            data-no-click={!isAvailable}
                            // tslint:disable-next-line: jsx-no-lambda & react-this-binding-issue
                            onClick={() => { dimension.selectId && dimension.selectedValue && updatePricing(dimension.selectId, dimension.selectedValue, dimension.dimensionLabel, true); }}
                            // tslint:disable-next-line: jsx-no-lambda & react-this-binding-issue
                            onKeyUp={(e) => { dimension.selectId && dimension.selectedValue && handleKeyPress(e, dimension.selectId, dimension.selectedValue, dimension.dimensionLabel); }}
                        >
                            <div className='row'>
                                <div className={`msc-variation-size${isAvailable ? '' : ' out-of-stock-variant'}`}>
                                    <div className='msc-variation-size__size-label'>
                                        {dimension.selectedValue}
                                    </div>
                                    <div className='msc-variation-size__dimension-label'>
                                        {dimension.dimensionLabel}
                                    </div>
                                </div>
                                {isAvailable ?
                                    <div className={dimension.discountAmount !== 0 ? 'msc-variation-total-price' : 'msc-variation-total-price-no-discount'}>
                                        {
                                            dimension && dimension.discountAmount !== undefined && dimension.dimensionPrice !== undefined
                                            ? returnValidPricing(dimension.discountAmount, dimension.dimensionPrice)
                                            : ''
                                        }
                                        <div className='msc-variation-price'>
                                            {dimension.percentDiff ? <span className='msc-variation-price-discount'>{dimension.percentDiff}% Off</span> : null}
                                            {dimension.discountAmount ? <span className='msc-variation-actual-price'>&nbsp;{props.formatCurrencyFunc(dimension.dimensionPrice)}</span> : null}
                                        </div>
                                    </div>
                                    :
                                    <div className='msc-variation-out-of-stock'>
                                        <div className='msc-variation-out-of-stock_text'>
                                            {props.outOfStockText}
                                        </div>
                                    </div>
                                }
                            </div>
                        </div>
                    );
                })}
            </ModalBody>
        </Modal>
    );
};
